import * as React from 'react'
import { Ban, FileSignature, Link2, Mail, PenLine, Smartphone, UserCheck } from 'lucide-react'

import {
  useShareLinks,
  useCreateShareLink,
  useRevokeShareLink,
  useSignedPdfUrl,
  useEstimateSignature,
  useSettings,
  useUsers,
  useSendEstimateEmail,
  useSendEstimateSms
} from '@/lib/queries.js'
import { Button } from '@/components/ui/button.jsx'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card.jsx'
import { Field, Input, Label } from '@/components/ui/field.jsx'
import { Select } from '@/components/ui/select.jsx'
import { Alert, Spinner } from '@/components/ui/feedback.jsx'
import { Badge } from '@/components/ui/badge.jsx'
import { CopyButton } from '@/components/CopyButton.jsx'
import { humanMessage, isWorkflow } from '@/lib/errors.js'
import { formatDateTime, money } from '@/lib/format.js'

const API_BASE = import.meta.env.VITE_API_URL

/**
 * The estimate-side Phase 2 surfaces (item 16a, surfaces 4-7).
 *
 * Everything here existed at the API layer from Session 1a and could not be
 * reached by a contractor. The through-line: an estimate is only worth
 * anything once a customer has SIGNED it, and until this file the product
 * could produce a signature only if someone issued a share token with curl.
 */

/* ==========================================================================
   Surface 6 — the per-estimate deposit override (D22.2)
   ========================================================================== */

/**
 * PREVIEW ARITHMETIC ONLY. Mirrors services/depositCalc.js deliberately, and
 * the server's answer is the one that counts: `deposit_amount` is computed and
 * FROZEN server-side at acceptance (D22.2), and every downstream reader — the
 * signed PDF, the deposit invoice — reads that frozen column.
 *
 * Duplicating a formula is a real cost and it is taken with eyes open, for the
 * same reason `previewTotals` in EstimateEditor already does it: D22.1 requires
 * the contractor to see the resulting figure BEFORE the estimate is sent, and a
 * round trip per keystroke to learn a number that is not yet stored anywhere
 * would be worse. If depositCalc's rule changes, this changes with it.
 */
function previewDeposit ({ grandTotal, type, value, tenantPercent, tenantMinimumCents }) {
  const totalCents = Math.round(Number(grandTotal ?? 0) * 100)
  if (!Number.isFinite(totalCents) || totalCents < 0) return null

  let raw
  if (type === 'flat') {
    // An explicit dollar figure is the contractor stating the number. The
    // minimum does NOT floor it — raising it silently would make the override
    // lie (depositCalc's stated reasoning).
    raw = Math.round(Number(value ?? 0) * 100)
  } else {
    const pct = type === 'percentage' ? Number(value ?? 0) : Number(tenantPercent ?? 30)
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) return null
    raw = Math.max(Math.round(totalCents * (pct / 100)), Number(tenantMinimumCents ?? 0))
  }
  if (!Number.isFinite(raw) || raw < 0) return null

  const deposit = Math.min(raw, totalCents)
  return { depositCents: deposit, balanceCents: totalCents - deposit }
}

/**
 * @param {object} props
 * @param {{deposit_type: string|null, deposit_value: number}} props.draft
 * @param {(fn: (d: object) => object) => void} props.setDraft
 * @param {number} props.grandTotal  the previewed estimate total, incl. tax
 * @param {boolean} props.editable
 */
export function DepositOverrideCard ({ draft, setDraft, grandTotal, editable }) {
  const { data: settings } = useSettings()

  const type = draft.deposit_type || ''
  const preview = previewDeposit({
    grandTotal,
    type,
    value: draft.deposit_value,
    tenantPercent: settings?.deposit.deposit_percent,
    tenantMinimumCents: settings?.deposit.deposit_minimum_cents
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Deposit</CardTitle>
        <p className="mt-1 text-sm text-ink-muted">
          What this customer pays up front.
        </p>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="deposit-override-type">Deposit for this estimate</Label>
          <Select
            id="deposit-override-type"
            value={type}
            disabled={!editable}
            onChange={e => setDraft(d => ({ ...d, deposit_type: e.target.value, deposit_value: 0 }))}
            data-testid="deposit-override-type"
          >
            <option value="">
              {settings
                ? `Workspace default (${settings.deposit.deposit_percent}%${settings.deposit.deposit_minimum_cents > 0 ? `, ${money(settings.deposit.deposit_minimum_cents / 100)} minimum` : ''})`
                : 'Workspace default'}
            </option>
            <option value="percentage">A percentage of this estimate</option>
            <option value="flat">A set dollar amount</option>
          </Select>
        </div>

        {type && (
          <Field
            id="deposit-override-value"
            label={type === 'percentage' ? 'Percentage' : 'Amount'}
            type="number" inputMode="decimal" min="0" step={type === 'percentage' ? '1' : '0.01'}
            max={type === 'percentage' ? '100' : undefined}
            value={draft.deposit_value ?? 0}
            disabled={!editable}
            onChange={e => setDraft(d => ({ ...d, deposit_value: e.target.value }))}
            hint={type === 'flat' ? 'Used exactly as typed. The workspace minimum does not apply.' : undefined}
            data-testid="deposit-override-value"
          />
        )}

        {/* D22.1: the resulting figure, before the estimate is sent. */}
        {preview && (
          <div className="rounded-md border border-line bg-surface-sunken p-3" data-testid="deposit-figure">
            <p className="text-sm">
              <span className="text-ink-muted">Due up front </span>
              <strong className="text-ink">{money(preview.depositCents / 100)}</strong>
            </p>
            <p className="mt-0.5 text-sm text-ink-muted">
              {preview.balanceCents > 0
                ? <>Balance {money(preview.balanceCents / 100)}</>
                : 'Nothing left to pay afterwards.'}
            </p>
            <p className="mt-1.5 text-xs text-ink-subtle">
              Locked in when the customer accepts. Changing the estimate before
              then changes this too.
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

/* ==========================================================================
   Surface 5 — signature status and the signed PDF (D18, D27.4, D27.5)
   ========================================================================== */

export function SignatureCard ({ estimateId }) {
  const { data: signature, isPending } = useEstimateSignature(estimateId)
  const { data: users = [] } = useUsers()
  const mint = useSignedPdfUrl()
  const [error, setError] = React.useState(null)

  if (isPending) return null
  if (!signature) return null // unsigned: ShareLinkCard is the surface that matters

  const facilitator = users.find(u => u.id === signature.facilitated_by)
  const inPerson = signature.capture_mode === 'in_person'

  async function openPdf () {
    setError(null)
    try {
      // Minted at the moment of the click. The URL is short-lived (D18), so
      // holding one in state would hand the user a link that stops working.
      const { url } = await mint.mutateAsync({ estimateId })
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) { setError(err) }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <CardTitle className="flex items-center gap-2">
          <FileSignature className="size-4 text-ink-subtle" aria-hidden="true" />
          Signed
        </CardTitle>
        <Badge tone="success">
          {signature.mark_type === 'initials' ? 'Initialled' : 'Signed'}
        </Badge>
      </CardHeader>
      <CardBody className="space-y-3">
        {error && <Alert tone="danger">{humanMessage(error)}</Alert>}

        <dl className="space-y-1.5 text-sm">
          <div className="flex gap-2">
            <dt className="shrink-0 text-ink-muted">By</dt>
            <dd className="min-w-0 break-words text-ink" data-testid="signer-name">
              {signature.signer_name ?? 'Name not recorded'}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="shrink-0 text-ink-muted">When</dt>
            <dd className="text-ink">{formatDateTime(signature.signed_at)}</dd>
          </div>
          {/* D27.5: the two modes evidence different things and this says which
              one happened rather than flattening them into "signed". An
              in-person record is never dressed up as a remote one. */}
          <div className="flex gap-2">
            <dt className="shrink-0 text-ink-muted">How</dt>
            <dd className="min-w-0 text-ink" data-testid="capture-mode">
              {inPerson
                ? <>In person{facilitator ? `, with ${facilitator.first_name} ${facilitator.last_name ?? ''}`.trimEnd() : ''}</>
                : 'Remotely, on their own device'}
            </dd>
          </div>
        </dl>

        <p className="text-xs text-ink-subtle">
          {inPerson
            ? 'Signed on a device you were holding, so the record shows who was present rather than where the link was sent.'
            : 'Signed from a link sent to the customer. The record includes their device and network details.'}
        </p>

        <Button variant="secondary" onClick={openPdf} loading={mint.isPending} data-testid="open-signed-pdf">
          Open the signed PDF
        </Button>
      </CardBody>
    </Card>
  )
}

/* ==========================================================================
   Surface 4 + 7 — share links, and the in-person hand-off (D21, D27.3)
   ========================================================================== */

const shareUrl = path => new URL(path, API_BASE).toString()

export function ShareLinkCard ({ estimate, signed }) {
  const estimateId = estimate?.id
  const { data: links = [], isPending } = useShareLinks(estimateId)
  const create = useCreateShareLink()
  const revoke = useRevokeShareLink()

  const [issued, setIssued] = React.useState(null)
  const [error, setError] = React.useState(null)

  if (!estimateId) return null

  const shareable = ['sent', 'viewed'].includes(estimate.status)
  const live = links.filter(l => l.is_active && !l.revoked_at)

  async function issue (captureMode) {
    setError(null)
    setIssued(null)
    try {
      const { share_link: link } = await create.mutateAsync({ estimateId, captureMode })
      const url = shareUrl(link.path)
      if (captureMode === 'in_person') {
        // D27.3: a SEPARATE TAB, and no signing UI inside the dashboard. The
        // contractor hands over a phone showing the public page; the dashboard
        // session must never be what the customer is holding.
        window.open(url, '_blank', 'noopener,noreferrer')
      }
      setIssued({ ...link, url, captureMode })
    } catch (err) { setError(err) }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PenLine className="size-4 text-ink-subtle" aria-hidden="true" />
          {signed ? 'Signing links' : 'Get it signed'}
        </CardTitle>
        <p className="mt-1 text-sm/relaxed text-ink-muted">
          {signed
            ? 'This estimate is signed, so every link for it was switched off automatically.'
            : 'Send the customer a link, or hand them your phone and let them sign on the spot.'}
        </p>
      </CardHeader>

      <CardBody className="space-y-4">
        {error && (
          <Alert tone={isWorkflow(error) ? 'warn' : 'danger'}>{humanMessage(error)}</Alert>
        )}

        {!shareable && !signed && (
          <Alert tone="warn" data-testid="not-shareable">
            {estimate.status === 'draft'
              ? 'Send the estimate first. A draft has no customer-facing version yet.'
              : `A ${estimate.status} estimate cannot be shared for signing.`}
          </Alert>
        )}

        {/* D21: the plaintext token exists in ONE response and is never
            recoverable. Shown once, like an API key, with copying made the
            obvious move rather than an afterthought. */}
        {issued && (
          <div className="rounded-md border border-brand/30 bg-brand-subtle p-3" data-testid="issued-link">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink">
                  {issued.captureMode === 'in_person'
                    ? 'Open in the other tab. Hand them the device.'
                    : 'Copy this now.'}
                </p>
                <p className="mt-1 break-all font-mono text-xs text-ink-muted">{issued.url}</p>
              </div>
              <CopyButton value={issued.url} size="sm" label="Copy link" data-testid="copy-share-link" />
            </div>
            <p className="mt-2 text-xs text-ink-subtle">
              {issued.captureMode === 'in_person'
                ? 'Your dashboard is still open in this tab. Close the other one when they are done and refresh here to see the signature.'
                : 'We cannot show this link again. If you lose it, issue another one.'}
            </p>
          </div>
        )}

        {shareable && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              onClick={() => issue('remote')}
              loading={create.isPending && create.variables?.captureMode === 'remote'}
              data-testid="issue-remote-link"
            >
              <Link2 /> Create a link to send
            </Button>
            <Button
              variant="secondary"
              onClick={() => issue('in_person')}
              loading={create.isPending && create.variables?.captureMode === 'in_person'}
              data-testid="issue-in-person-link"
            >
              <Smartphone /> Sign here, in person
            </Button>
          </div>
        )}

        {/* Item 17 (D28a). Distinct from "Create a link to send": that one
            hands the contractor a link to paste into their own text message;
            this one has MBOS send it, and records the send so signing later
            fills in D27.5's destination honestly instead of the Session 1a
            placeholder null. */}
        {shareable && (
          <div className="grid gap-3 lg:grid-cols-2">
            <SendEstimateEmailInline estimateId={estimateId} />
            <SendEstimateSmsInline estimateId={estimateId} />
          </div>
        )}

        {isPending
          ? <Spinner label="Loading links" />
          : live.length > 0
            ? (
              <ul className="divide-y divide-line rounded-md border border-line" data-testid="share-link-list">
                {live.map(link => (
                  <li key={link.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 text-sm text-ink">
                        {link.capture_mode === 'in_person'
                          ? <><UserCheck className="size-3.5 text-ink-subtle" aria-hidden="true" /> In person</>
                          : <><Link2 className="size-3.5 text-ink-subtle" aria-hidden="true" /> Sent link</>}
                      </p>
                      <p className="mt-0.5 text-xs text-ink-subtle">
                        Expires {formatDateTime(link.expires_at)}
                        {link.last_viewed_at ? ` · opened ${formatDateTime(link.last_viewed_at)}` : ' · not opened yet'}
                      </p>
                    </div>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => revoke.mutate({ estimateId, linkId: link.id })}
                      disabled={revoke.isPending}
                      data-testid="revoke-share-link"
                    >
                      <Ban /> Switch off
                    </Button>
                  </li>
                ))}
              </ul>
              )
            : shareable
              ? <p className="text-sm text-ink-subtle">No live links.</p>
              : null}
      </CardBody>
    </Card>
  )
}

function SendEstimateSmsInline ({ estimateId }) {
  const [expanded, setExpanded] = React.useState(false)
  const [to, setTo] = React.useState('')
  const [sent, setSent] = React.useState(false)
  const send = useSendEstimateSms()

  async function submit (event) {
    event.preventDefault()
    setSent(false)
    try {
      await send.mutateAsync({ estimateId, to: to.trim() })
      setSent(true)
      setTo('')
    } catch {}
  }

  if (!expanded) {
    return (
      <Button variant="secondary" onClick={() => setExpanded(true)} data-testid="open-send-sms">
        <Smartphone /> Text it to them
      </Button>
    )
  }

  return (
    <form onSubmit={submit} className="space-y-2 rounded-md border border-line p-3">
      {send.error && <Alert tone="danger">{humanMessage(send.error)}</Alert>}
      {sent && <Alert tone="success">Text sent.</Alert>}
      <Label htmlFor="send-estimate-sms-to">Customer's phone</Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id="send-estimate-sms-to"
          type="tel"
          value={to}
          onChange={e => setTo(e.target.value)}
          placeholder="+15550101234"
          autoCapitalize="none" autoCorrect="off"
          required
          disabled={send.isPending}
          data-testid="send-estimate-sms-to"
        />
        <Button type="submit" loading={send.isPending} disabled={!to.trim()} data-testid="send-estimate-sms-submit">
          <Smartphone /> Send
        </Button>
      </div>
    </form>
  )
}

/**
 * The item 17 send action. An expand-on-click inline form rather than a
 * button that fires immediately — sending needs a destination, and a modal
 * for one email field would be more chrome than the task warrants.
 */
function SendEstimateEmailInline ({ estimateId }) {
  const [expanded, setExpanded] = React.useState(false)
  const [to, setTo] = React.useState('')
  const [sent, setSent] = React.useState(false)
  const send = useSendEstimateEmail()

  async function submit (event) {
    event.preventDefault()
    setSent(false)
    try {
      await send.mutateAsync({ estimateId, to: to.trim() })
      setSent(true)
      setTo('')
    } catch { /* rendered from send.error below */ }
  }

  if (!expanded) {
    return (
      <Button variant="secondary" onClick={() => setExpanded(true)} data-testid="open-send-email">
        <Mail /> Email it to them
      </Button>
    )
  }

  return (
    <form onSubmit={submit} className="space-y-2 rounded-md border border-line p-3">
      {send.error && <Alert tone="danger">{humanMessage(send.error)}</Alert>}
      {sent && <Alert tone="success">Sent.</Alert>}
      <Label htmlFor="send-estimate-email-to">Customer's email</Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id="send-estimate-email-to"
          type="email"
          value={to}
          onChange={e => setTo(e.target.value)}
          placeholder="customer@example.com"
          autoCapitalize="none" autoCorrect="off"
          required
          disabled={send.isPending}
          data-testid="send-estimate-email-to"
        />
        <Button type="submit" loading={send.isPending} disabled={!to.trim()} data-testid="send-estimate-email-submit">
          <Mail /> Send
        </Button>
      </div>
    </form>
  )
}
